import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../../utils/cn";
import {
  CONTROL_BASE,
  CONTROL_VARIANT_CLASSES,
  type ControlVariant,
} from "../controlStyles";

export type IconButtonVariant = ControlVariant;
export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The icon node, e.g. a Phosphor glyph. Sized via CSS. */
  icon: ReactNode;
  /** Required: an icon-only button must have an accessible name. */
  "aria-label": string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

// Square boxes matching the prototype (28/36/44); ghost is the common default.
const sizes: Record<IconButtonSize, string> = {
  sm: "size-7 rounded-sm-plus [&_svg]:size-4",
  md: "size-9 rounded-md [&_svg]:size-[18px]",
  lg: "size-11 rounded-lg [&_svg]:size-5",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      variant = "ghost",
      size = "md",
      className,
      type = "button",
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          CONTROL_BASE,
          CONTROL_VARIANT_CLASSES[variant],
          sizes[size],
          className,
        )}
        {...rest}
      >
        {icon}
      </button>
    );
  },
);
